import React from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/Card';
import { Table, Download, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';

interface TableRendererProps {
  data: Record<string, any>[];
  title?: string;
  className?: string;
}

export const TableRenderer: React.FC<TableRendererProps> = ({
  data,
  title,
  className,
}) => {
  if (!data || data.length === 0) {
    return (
      <Card className={className}>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 bg-secondary-100 rounded-2xl flex items-center justify-center mb-4">
              <Table className="w-8 h-8 text-secondary-400" />
            </div>
            <p className="text-secondary-500 font-medium">No data available</p>
            <p className="text-sm text-secondary-400 mt-1">Try adjusting your query parameters</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const headers = Object.keys(data[0]);

  return (
    <Card className={className}>
      {title && (
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-secondary-100 to-secondary-200 rounded-xl flex items-center justify-center">
                <Table className="w-5 h-5 text-secondary-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-secondary-900">{title}</h3>
                <p className="text-sm text-secondary-600 font-medium">
                  {data.length} row{data.length !== 1 ? 's' : ''} × {headers.length} column{headers.length !== 1 ? 's' : ''}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors">
                <Filter className="w-4 h-4" />
              </button>
              <button className="p-2 text-secondary-500 hover:text-secondary-700 hover:bg-secondary-100 rounded-lg transition-colors">
                <Download className="w-4 h-4" />
              </button>
            </div>
          </div>
        </CardHeader>
      )}
      <CardContent padding="none">
        <div className="overflow-x-auto">
          <div className="max-h-96 overflow-y-auto">
            <table className="w-full">
              <thead className="sticky top-0 z-10">
                <tr className="border-b border-secondary-200 bg-gradient-to-r from-secondary-50 to-secondary-100/50 backdrop-blur-sm">
                  {headers.map((header, index) => (
                    <th
                      key={header}
                      className={cn(
                        "px-6 py-4 text-left text-xs font-bold text-secondary-700 uppercase tracking-wider",
                        index === 0 && "rounded-tl-lg",
                        index === headers.length - 1 && "rounded-tr-lg"
                      )}
                    >
                      <div className="flex items-center gap-2">
                        {header.replace(/_/g, ' ')}
                        <div className="w-1 h-4 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full opacity-60"></div>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-100">
                {data.map((row, index) => (
                  <tr
                    key={index}
                    className={cn(
                      'hover:bg-gradient-to-r hover:from-primary-50/50 hover:to-secondary-50/50 transition-all duration-200',
                      index % 2 === 0 ? 'bg-white' : 'bg-secondary-25/50'
                    )}
                  >
                    {headers.map((header, cellIndex) => (
                      <td
                        key={header}
                        className="px-6 py-4 text-sm text-secondary-900 whitespace-nowrap font-medium"
                      >
                        <div className={cn(
                          "flex items-center",
                          cellIndex === 0 && "font-semibold text-secondary-900"
                        )}>
                          {row[header] !== null && row[header] !== undefined
                            ? String(row[header])
                            : (
                              <span className="text-secondary-400 italic">—</span>
                            )}
                        </div>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Table footer with summary */}
        <div className="border-t border-secondary-200 bg-gradient-to-r from-secondary-50/50 to-white px-6 py-3">
          <div className="flex items-center justify-between text-xs text-secondary-600">
            <span className="font-medium">
              Showing {data.length} result{data.length !== 1 ? 's' : ''}
            </span>
            <span>
              Updated just now
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};